import React from 'react';
import {
  CheckCircle, Warning, ShieldWarning, ClockCountdown,
  UserCircleMinus, ArrowRight
} from '@phosphor-icons/react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer
} from 'recharts';

const STATUS_BADGE = {
  Verified: { cls: 'text-emerald-400 bg-emerald-500/10', icon: CheckCircle },
  'Actual Unverified': { cls: 'text-amber-400 bg-amber-500/10', icon: Warning },
  'Default Applied': { cls: 'text-red-400 bg-red-500/10', icon: ShieldWarning },
};

const SHARING_BADGE = {
  Active: 'text-emerald-400 bg-emerald-500/10',
  Pending: 'text-amber-400 bg-amber-500/10',
  'Non-Responsive': 'text-red-400 bg-red-500/10',
};

export default function CbamSuppliers({ data }) {
  if (!data) return null;
  const { suppliers, summary } = data;

  // Funnel data
  const funnelData = [
    { name: 'Total Suppliers', value: summary.total, fill: '#60a5fa' },
    { name: 'Active Sharing', value: summary.active, fill: '#4ade80' },
    { name: 'Verified', value: summary.verified, fill: '#22c55e' },
  ];

  // Carbon intensity ranking
  const intensityData = [...suppliers]
    .sort((a, b) => b.embedded_intensity_tco2e_t - a.embedded_intensity_tco2e_t)
    .map(s => ({
      name: s.name?.length > 18 ? s.name.slice(0, 18) + '...' : s.name,
      intensity: s.embedded_intensity_tco2e_t,
      fill: s.verification_status === 'Verified' ? '#4ade80' : s.verification_status === 'Default Applied' ? '#ef4444' : '#facc15',
    }));

  // Missing data leaderboard
  const missingData = [...suppliers]
    .filter(s => s.data_completeness_pct < 80)
    .sort((a, b) => a.data_completeness_pct - b.data_completeness_pct);

  return (
    <div className="space-y-5 animate-fade-in" data-testid="cbam-suppliers">
      {/* Summary Tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-surface border border-border rounded-lg p-4 text-center">
          <p className="text-2xl font-heading font-bold text-text-primary">{summary.total}</p>
          <p className="text-[11px] font-body text-text-secondary uppercase tracking-wider mt-1">Total Suppliers</p>
        </div>
        <div className="bg-surface border border-border rounded-lg p-4 text-center">
          <p className="text-2xl font-heading font-bold text-emerald-400">{summary.active}</p>
          <p className="text-[11px] font-body text-text-secondary uppercase tracking-wider mt-1">Active Sharing</p>
        </div>
        <div className="bg-surface border border-border rounded-lg p-4 text-center">
          <p className="text-2xl font-heading font-bold text-amber-400">{summary.avg_completeness_pct}%</p>
          <p className="text-[11px] font-body text-text-secondary uppercase tracking-wider mt-1">Avg Completeness</p>
        </div>
        <div className="bg-surface border border-border rounded-lg p-4 text-center">
          <p className="text-2xl font-heading font-bold text-red-400">{summary.default_applied}</p>
          <p className="text-[11px] font-body text-text-secondary uppercase tracking-wider mt-1">Default Applied</p>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Response Funnel */}
        <div className="bg-surface border border-border rounded-lg p-5" data-testid="cbam-supplier-funnel">
          <h3 className="text-sm font-body font-semibold text-text-primary mb-4">Supplier Response Funnel</h3>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={funnelData} margin={{ left: 10, right: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" horizontal={false} />
              <XAxis dataKey="name" tick={{ fill: '#94a3b8', fontSize: 11 }} />
              <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} />
              <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, fontSize: 12 }} />
              <Bar dataKey="value" radius={[4, 4, 0, 0]} barSize={40}>
                {funnelData.map((e, i) => <Bar key={i} dataKey="value" fill={e.fill} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Carbon Intensity Ranking */}
        <div className="bg-surface border border-border rounded-lg p-5" data-testid="cbam-supplier-intensity">
          <h3 className="text-sm font-body font-semibold text-text-primary mb-4">Precursor Emission Intensity (tCO2e/t)</h3>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={intensityData} layout="vertical" margin={{ left: 10, right: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" horizontal={false} />
              <XAxis type="number" tick={{ fill: '#94a3b8', fontSize: 11 }} />
              <YAxis type="category" dataKey="name" tick={{ fill: '#94a3b8', fontSize: 10 }} width={110} />
              <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, fontSize: 12 }} formatter={(v) => [`${v} tCO2e/t`, 'Intensity']} />
              <Bar dataKey="intensity" radius={[0, 4, 4, 0]} barSize={14}>
                {intensityData.map((e, i) => <Bar key={i} dataKey="intensity" fill={e.fill} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Missing Data Leaderboard */}
      {missingData.length > 0 && (
        <div className="bg-surface border border-border rounded-lg p-5" data-testid="cbam-missing-data">
          <h3 className="text-sm font-body font-semibold text-text-primary mb-1">Missing Data Leaderboard</h3>
          <p className="text-[11px] font-body text-text-secondary mb-4">Suppliers below 80% data completeness — prioritise engagement</p>
          <div className="space-y-3">
            {missingData.map((s, i) => (
              <div key={i} className="border border-border rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <UserCircleMinus size={16} className="text-text-secondary" />
                    <span className="text-sm font-body font-medium text-text-primary">{s.name}</span>
                    <span className="text-[10px] font-body text-text-secondary">({s.country})</span>
                  </div>
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${SHARING_BADGE[s.data_sharing_status] || 'text-text-secondary'}`}>{s.data_sharing_status}</span>
                </div>
                <div className="w-full bg-white/5 rounded-full h-2 mb-1">
                  <div className={`h-2 rounded-full ${s.data_completeness_pct >= 50 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${s.data_completeness_pct}%` }} />
                </div>
                <div className="flex items-center justify-between text-[11px] font-body text-text-secondary">
                  <span>Completeness: {s.data_completeness_pct}%</span>
                  <span>Precursor: {s.precursor_type}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Full Supplier Table */}
      <div className="bg-surface border border-border rounded-lg p-5" data-testid="cbam-supplier-table">
        <h3 className="text-sm font-body font-semibold text-text-primary mb-4">All Precursor Suppliers</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-xs font-body">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-2 text-text-secondary font-semibold">Supplier</th>
                <th className="text-left py-2 text-text-secondary font-semibold">Precursor</th>
                <th className="text-left py-2 text-text-secondary font-semibold">Country</th>
                <th className="text-center py-2 text-text-secondary font-semibold">Status</th>
                <th className="text-center py-2 text-text-secondary font-semibold">Verification</th>
                <th className="text-right py-2 text-text-secondary font-semibold">Intensity</th>
                <th className="text-right py-2 text-text-secondary font-semibold">C-Price</th>
                <th className="text-right py-2 text-text-secondary font-semibold">Completeness</th>
                <th className="text-left py-2 text-text-secondary font-semibold">Last Submission</th>
              </tr>
            </thead>
            <tbody>
              {(suppliers || []).map((s, i) => {
                const vBadge = STATUS_BADGE[s.verification_status] || STATUS_BADGE['Actual Unverified'];
                const VIcon = vBadge?.icon || Warning;
                return (
                  <tr key={i} className="border-b border-border/50 hover:bg-white/[0.02]">
                    <td className="py-2.5 text-text-primary font-medium">{s.name}</td>
                    <td className="py-2.5 text-text-secondary">{s.precursor_type}</td>
                    <td className="py-2.5 text-text-secondary">{s.country}</td>
                    <td className="py-2.5 text-center">
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${SHARING_BADGE[s.data_sharing_status] || ''}`}>{s.data_sharing_status}</span>
                    </td>
                    <td className="py-2.5 text-center">
                      <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${vBadge?.cls || ''}`}>
                        <VIcon size={10} weight="fill" />{s.verification_status}
                      </span>
                    </td>
                    <td className="py-2.5 text-right font-semibold text-text-primary">{s.embedded_intensity_tco2e_t}</td>
                    <td className="py-2.5 text-right text-text-secondary">{s.carbon_price_eur_tco2e > 0 ? `EUR ${s.carbon_price_eur_tco2e}` : '-'}</td>
                    <td className="py-2.5 text-right">
                      <span className={`font-semibold ${s.data_completeness_pct >= 80 ? 'text-emerald-400' : s.data_completeness_pct >= 50 ? 'text-amber-400' : 'text-red-400'}`}>{s.data_completeness_pct}%</span>
                    </td>
                    <td className="py-2.5 text-text-secondary">{s.last_submission ? new Date(s.last_submission).toLocaleDateString() : 'Never'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
